/**
 * 明阳智能 - 检查项记录管理接口（task_name: mingyang_check）
 * 协议来源：Web管理后台-检查项记录管理.md
 */
import { callMingyangTask } from './client'
import { ExportTaskBrief, ExportTaskDetail } from './project'

const TASK = 'mingyang_check' as const

// ------------------------------------------------------------
// 状态枚举
// ------------------------------------------------------------

export type CheckRecordStatus = 0 | 1 | 2 | 3 | 4 | 5

export type CheckStatusAction =
  | 'submit_rectify'
  | 'submit_appeal'
  | 'review_pass'
  | 'review_reject'
  | 'appeal_pass'
  | 'appeal_reject'

// ------------------------------------------------------------
// 5.1 记录统计
// ------------------------------------------------------------

export interface CheckStatsResponse {
  total: number
  passed: number
  failed: number
  pending: number
  manual_reviewing: number
  appealing: number
}

export function fetchCheckStats(dispatch: (...args: any[]) => any) {
  return callMingyangTask<CheckStatsResponse>(dispatch, TASK, 'stats', {})
}

// ------------------------------------------------------------
// 5.2 记录列表
// ------------------------------------------------------------

export interface CheckListParams {
  base_name?: string
  project_name?: string
  project_number?: string
  machine_model?: string
  process_name?: string
  inspection?: string
  start_time?: string
  end_time?: string
  status?: CheckRecordStatus | CheckRecordStatus[] | null
  page?: number
  page_size?: number
  /** 当前操作者 user_id — 后端按身份过滤数据可见性,不传 → 后端按"未登录"
   *  行为兜底.callsite 用 selectGroupAndUser(state) 解构后传入 */
  user_id?: string
  /** 当前操作者团队身份(myRole 整数枚举):
   *    -1=全部成员/未确定, 0=普通成员, 1=主管理员, 2=管理员, 3=部门管理员
   *  后端按 role 决定能看哪些记录.callsite 用 selectGroupAndUser(state) 解构后传入 */
  role?: number
}

export interface CheckListItem {
  check_id: number
  base_name: string
  project_name: string
  project_number: string
  machine_model: string
  process_name: string
  inspection: string
  user_ids: string
  user_names: string
  operator_name: string
  status: CheckRecordStatus
  status_label: string
  update_time: string
}

export interface CheckListResponse {
  total: number
  page: number
  page_size: number
  list: CheckListItem[]
  /**
   * 后端按请求信封顶层 user_id 自动判定的角色：0=普通用户, 1=管理员（预留 2/3）。
   * 前端用 `role >= 1` 判定管理员渲染审核/驳回类按钮。
   * 兼容期可能为 undefined（旧后端 / 灰度过渡）→ 调用方 fallback 到 useIsAdmin()。
   * ⚠ 与请求 params 里的 `role` 无关：后端忽略前端传的 role，自动判定。
   */
  role?: number
}

export async function fetchCheckList(
  dispatch: (...args: any[]) => any,
  params: CheckListParams = {}
): Promise<CheckListResponse> {
  const data = await callMingyangTask<CheckListResponse>(
    dispatch,
    TASK,
    'list',
    {
      base_name: params.base_name || '',
      project_name: params.project_name || '',
      project_number: params.project_number || '',
      machine_model: params.machine_model || '',
      process_name: params.process_name || '',
      inspection: params.inspection || '',
      start_time: params.start_time || '',
      end_time: params.end_time || '',
      status: params.status ?? null,
      page: params.page || 1,
      page_size: params.page_size || 20,
      user_id: params.user_id || '',
      // role 是整数枚举(含 0=普通成员),禁用 || 兜底以免吞 0;
      // 缺省 -1 跟 reducer initialState 同口径
      role: params.role ?? -1
    }
  )
  // 字段兜底：id ↔ check_id；last_operator_name ↔ operator_name；透传顶层 role（后端权限收口）
  return {
    ...data,
    role:
      typeof (data as any)?.role === 'number' ? (data as any).role : undefined,
    list: ((data && data.list) || []).map((r: any) => ({
      ...r,
      check_id: r.check_id ?? r.id,
      operator_name: r.operator_name ?? r.last_operator_name ?? '',
      user_ids: r.user_ids ?? '',
      user_names: r.user_names ?? ''
    }))
  }
}

// ------------------------------------------------------------
// 5.3 记录详情
// ------------------------------------------------------------

export type AiAnalysisStatus = 0 | 1 | 2 | 3 // 合格/不合格/数据质量问题/解析异常

export interface CheckMediaItem {
  photo_seq: number
  media_type: 0 | 1 // 0-照片 1-视频
  photo_file: string
  photo_url: string
  photo_time: string
}

/** AI 分析 result 结构化字段(2026-05-20 起后端从 URL 字符串改为对象) */
export interface AiAnalysisResultObject {
  name: string
  value: string
}

export interface AiAnalysisResultRule {
  name: string
  is_compliant: boolean
  reason?: string
}

export interface AiAnalysisResultData {
  objects?: AiAnalysisResultObject[]
  rules?: AiAnalysisResultRule[]
  all_compliant?: boolean
  total_objects?: number
  total_rules?: number
  standard?: any[]
  retrieval?: Record<string, any>
  usage?: Record<string, any>
  raw_output?: string
}

export interface AiAnalysisItem {
  ai_analysis_id: number
  round: number
  status: AiAnalysisStatus
  status_label: string
  photos: string
  videos: string
  cost_time: number
  /** 新版本是对象,旧数据是 URL 字符串;前端按类型分支处理 */
  result: AiAnalysisResultData | string | null
  /** 独立的 OSS 结果文件 URL,新版本拆出来 */
  result_url?: string
  create_time: string
  media_list: CheckMediaItem[]
}

export interface KnowledgeRuleSummary {
  rule_detail_id: number
  obj: string
  spec: string
  uncertainty: string
  range: string
  coverage: string
  rule: string
}

export interface KnowledgeRulesBlock {
  knowledge_id: number
  inspection: string
  media_num: { photo: number; video: number }
  watermark_rule: string
  rule_details: KnowledgeRuleSummary[]
}

export interface OperationLogItem {
  log_id: number
  action: string
  action_label: string
  operator_id: string
  operator_name: string
  status_before: number | null
  status_after: number | null
  detail: Record<string, any>
  create_time: string
}

export interface CheckDetailBasic {
  check_id: number
  base_name: string
  project_name: string
  project_number: string
  machine_model: string
  process_name: string
  inspection: string
  knowledge_id: number
  user_ids: string
  user_names: string
  status: CheckRecordStatus
  status_label: string
  create_time: string
  update_time: string
  operator_name: string
}

export interface CheckDetailResponse {
  basic: CheckDetailBasic
  ai_analysis_list: AiAnalysisItem[]
  knowledge_rules: KnowledgeRulesBlock
  operation_logs: OperationLogItem[]
  /**
   * 后端按请求信封顶层 user_id 自动判定的角色（与列表口径一致）。
   * 前端用 `permission.role >= 1` 判定管理员渲染审核/驳回类按钮。
   * 兼容期 permission 可能为 undefined → 调用方 fallback 到 useIsAdmin()。
   */
  permission?: {
    /** 0=普通用户, 1=管理员（预留 2/3） */
    role: number
  }
}

export async function fetchCheckDetail(
  dispatch: (...args: any[]) => any,
  // 雪花 ID 17-19 位,超 Number.MAX_SAFE_INTEGER;允许 string 形态以保留精度
  check_id: number | string
): Promise<CheckDetailResponse> {
  // 后端入参字段是 id，同时兼容 check_id
  const d = await callMingyangTask<any>(dispatch, TASK, 'detail', {
    id: check_id,
    check_id
  })
  // 字段兜底：
  //  - basic.id ↔ basic.check_id；last_operator_name ↔ operator_name
  //  - ai_analysis_list[].id ↔ ai_analysis_id；其 media_list[].id ↔ photo_seq
  //  - knowledge_rules.id ↔ knowledge_id；其 rule_details[].id ↔ rule_detail_id
  //  - operation_logs[].id ↔ log_id；last_operator_name ↔ operator_name
  const basic = d?.basic || {}
  return {
    ...d,
    // 透传后端权限收口字段(d.permission.role)，缺省时调用方走 useIsAdmin() fallback
    permission:
      d?.permission && typeof d.permission.role === 'number'
        ? { role: d.permission.role }
        : undefined,
    basic: {
      ...basic,
      check_id: basic.check_id ?? basic.id ?? check_id,
      operator_name: basic.operator_name ?? basic.last_operator_name ?? '',
      user_ids: basic.user_ids ?? '',
      user_names: basic.user_names ?? ''
    },
    ai_analysis_list: Array.isArray(d?.ai_analysis_list)
      ? d.ai_analysis_list.map((a: any) => ({
          ...a,
          ai_analysis_id: a.ai_analysis_id ?? a.id,
          media_list: Array.isArray(a.media_list)
            ? a.media_list.map((m: any) => ({
                ...m,
                photo_seq: m.photo_seq ?? m.id,
                // 字段兜底：接口返回 url，前端用 photo_url；
                // media_type 接口未返回时按图片处理（0）
                photo_url: m.photo_url ?? m.url ?? m.photo_file ?? '',
                media_type: typeof m.media_type === 'number' ? m.media_type : 0,
                photo_time: m.photo_time ?? m.create_time ?? ''
              }))
            : []
        }))
      : [],
    knowledge_rules: d?.knowledge_rules
      ? {
          ...d.knowledge_rules,
          knowledge_id: d.knowledge_rules.knowledge_id ?? d.knowledge_rules.id,
          rule_details: Array.isArray(d.knowledge_rules.rule_details)
            ? d.knowledge_rules.rule_details.map((r: any) => ({
                ...r,
                rule_detail_id: r.rule_detail_id ?? r.id
              }))
            : []
        }
      : null,
    operation_logs: Array.isArray(d?.operation_logs)
      ? d.operation_logs.map((l: any) => ({
          ...l,
          log_id: l.log_id ?? l.id,
          operator_name: l.operator_name ?? l.last_operator_name ?? ''
        }))
      : []
  }
}

// ------------------------------------------------------------
// 5.4 状态变更
// ------------------------------------------------------------

/**
 * submit_rectify 时必填的整改媒体资源子项。
 * 字段对齐 5.4 接口文档：
 *   - photo_seq: 全局唯一照片 ID
 *   - photo_file: 完整 URL（后端自动提取 OSS key 存 media.photo_file）
 *   - photo_content: 水印 JSON 字符串（picker 暂未返回，置空字符串）
 *   - media_type: 0-照片 1-视频
 *   - photo_time: 拍摄时间 YYYY-MM-DD HH:mm:ss
 *     picker 选的是已存在的网图，后端 ON CONFLICT DO NOTHING，前端用当前时间兜底
 */
export interface MediaResourceParam {
  photo_seq: number
  photo_file: string
  photo_content?: string
  media_type: 0 | 1
  photo_time: string
}

export interface UpdateCheckStatusParams {
  check_id: number
  action: CheckStatusAction
  remark?: string
  /** submit_rectify 时必填，其他 action 不传 */
  media_resources?: MediaResourceParam[]
  group_id: string
  user_id: string
}

export function updateCheckStatus(
  dispatch: (...args: any[]) => any,
  params: UpdateCheckStatusParams
) {
  return callMingyangTask<{
    success: boolean
    status_before: CheckRecordStatus
    status_after: CheckRecordStatus
  }>(dispatch, TASK, 'update_status', {
    ...params,
    id: params.check_id
  })
}

// ------------------------------------------------------------
// 5.5 筛选下拉选项(支持 filters 联动)
// ------------------------------------------------------------

export type CheckFilterField =
  | 'base_name'
  | 'project_name'
  | 'project_number'
  | 'machine_model'
  | 'process_name'
  | 'inspection'
  | 'status'

/** 联动筛选条件:key 为维度名,value 为已选值数组。status 是 int[]。 */
export interface CheckFilterCascade {
  base_name?: string[]
  project_name?: string[]
  project_number?: string[]
  machine_model?: string[]
  process_name?: string[]
  inspection?: string[]
  status?: CheckRecordStatus[]
}

export interface CheckFilterOptionsResponse {
  options: string[] | Array<{ value: number; label: string }>
}

export function fetchCheckFilterOptions(
  dispatch: (...args: any[]) => any,
  params: {
    field: CheckFilterField
    keyword?: string
    limit?: number
    /** 已选筛选条件,后端按除 field 外的条件 cascade 收窄候选选项 */
    filters?: CheckFilterCascade
  }
) {
  // 把每个 key 的空数组过滤掉,避免后端把空 IN 当成命中 0 行(语义跟"不传"等价)
  const cleanFilters: CheckFilterCascade = {}
  if (params.filters) {
    for (const k of Object.keys(params.filters) as Array<
      keyof CheckFilterCascade
    >) {
      const v = params.filters[k]
      if (Array.isArray(v) && v.length > 0) {
        // TS 这里需要 any 桥接 — filters 是 union 类型
        ;(cleanFilters as any)[k] = v
      }
    }
  }
  return callMingyangTask<CheckFilterOptionsResponse>(
    dispatch,
    TASK,
    'filter_options',
    {
      field: params.field,
      keyword: params.keyword || '',
      limit: params.limit || 10,
      ...(Object.keys(cleanFilters).length > 0 ? { filters: cleanFilters } : {})
    }
  )
}

// ------------------------------------------------------------
// 5.6 发起导出
// ------------------------------------------------------------

export interface CheckExportParams {
  base_name?: string
  project_name?: string
  project_number?: string
  machine_model?: string
  process_name?: string
  inspection?: string
  start_time?: string
  end_time?: string
  status?: CheckRecordStatus | CheckRecordStatus[] | null
  group_id: string
  user_id: string
}

export function startCheckExport(
  dispatch: (...args: any[]) => any,
  params: CheckExportParams
) {
  return callMingyangTask<ExportTaskBrief>(dispatch, TASK, 'export', {
    base_name: params.base_name || '',
    project_name: params.project_name || '',
    project_number: params.project_number || '',
    machine_model: params.machine_model || '',
    process_name: params.process_name || '',
    inspection: params.inspection || '',
    start_time: params.start_time || '',
    end_time: params.end_time || '',
    status: params.status ?? null,
    group_id: params.group_id,
    user_id: params.user_id
  })
}

// ------------------------------------------------------------
// 5.7 导出状态
// ------------------------------------------------------------

export function fetchCheckExportStatus(
  dispatch: (...args: any[]) => any,
  export_id: number
) {
  return callMingyangTask<ExportTaskDetail>(dispatch, TASK, 'export_status', {
    id: export_id,
    export_id
  })
}

// ------------------------------------------------------------
// 5.8 导出历史
// ------------------------------------------------------------

export interface CheckExportListResponse {
  total: number
  list: ExportTaskDetail[]
}

export function fetchCheckExportList(
  dispatch: (...args: any[]) => any,
  params: { user_id: string; page?: number; page_size?: number }
) {
  return callMingyangTask<CheckExportListResponse>(
    dispatch,
    TASK,
    'export_list',
    {
      user_id: params.user_id,
      page: params.page || 1,
      page_size: params.page_size || 10
    }
  )
}
